// Loader.jsx
import React from "react";

export default function Loader({
  variant = "inline",          // "inline" | "button" | "overlay"
  size = 24,                  // pixel size of spinner
  label = "Loading...",       // accessible label (shown for inline/overlay)
  className = "",             // extra Tailwind classes
}) {
  const stroke = "currentColor";

  const Spinner = (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      className="animate-spin"
      aria-hidden="true"
    >
      {/* Track */}
      <circle
        cx="12"
        cy="12"
        r="10"
        stroke={stroke}
        strokeWidth="4"
        fill="none"
        className="opacity-20"
      />
      {/* Arc */}
      <path
        d="M22 12a10 10 0 0 1-10 10"
        stroke={stroke}
        strokeWidth="4"
        strokeLinecap="round"
        fill="none"
      />
    </svg>
  );

  // Respect reduced motion (stop animation)
  const reducedMotionStyles = `
    @media (prefers-reduced-motion: reduce) {
      .animate-spin { animation: none; }
    }
  `;

  if (variant === "button") {
    return (
      <>
        <style>{reducedMotionStyles}</style>
        <span
          className={[
            "inline-flex items-center gap-2",
            "text-white", // inherit on a primary button
            className,
          ].join(" ")}
          role="status"
          aria-label={label}
        >
          {Spinner}
          <span className="sr-only">{label}</span>
        </span>
      </>
    );
  }

  if (variant === "overlay") {
    return (
      <>
        <style>{reducedMotionStyles}</style>
        <div
          className={[
            "fixed inset-0 z-50 grid place-items-center",
            "bg-white/60 backdrop-blur-sm",
            "dark:bg-neutral-900/60",
            className,
          ].join(" ")}
          role="status"
          aria-live="polite"
          aria-label={label}
        >
          <div className="flex items-center gap-3 p-4 rounded-2xl shadow-sm bg-white/80 dark:bg-neutral-900/70">
            <div className="text-sky-600 dark:text-sky-400">{Spinner}</div>
            <div className="text-neutral-700 dark:text-neutral-200 font-medium">
              {label}
            </div>
          </div>
        </div>
      </>
    );
  }

  // inline (default)
  return (
    <>
      <style>{reducedMotionStyles}</style>
      <div
        className={[
          "inline-flex items-center gap-2 text-neutral-700 dark:text-neutral-200",
          className,
        ].join(" ")}
        role="status"
        aria-live="polite"
        aria-label={label}
      >
        {Spinner}
        <span className="text-sm">{label}</span>
      </div>
    </>
  );
}
